import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useCreateProduct, usePostUSalesUnit, useUploadProductImage } from "../../../../api/product";
import { DEFAULT_UNIT_TYPE, DISABLE_IS_ROOT_PRODUCT_BUTTON } from "../../../../config/constants";
import { IsRootProduct } from "../../../../config/enums/ProductType.enum";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import { ProductRegisterModalProps } from "../../../product/productRegister/types/productTypes";
import { ProductRegisterOptions } from "../services/ProductRegisterOptions";
import { ProductFormData, ProductRegisterViewModel, ProductUnit } from "../types/productRegisterView.types";

const emptyUnit = (): ProductUnit => ({
  sales_unit_name: "",
  sales_unit_size: "",
  sales_unit_type: DEFAULT_UNIT_TYPE,
  ean_barcode: ""
});

/**
 * Owns every piece of state, data fetching and mutation handling the
 * register form. Sales units are a live-editable array of drafts — each
 * card's fields write straight into `salesUnits`, matching the Lovable
 * prototype's `RegisterProductDialog` (no separate "compose in a mini-form,
 * then Add" step, unlike the earlier version of this hook and the legacy
 * modal it was ported from). Nothing is sent to the server until the whole
 * product is saved, same as before.
 */
export const useProductRegisterViewModel = ({
  visibleModal,
  setVisibleModal
}: ProductRegisterModalProps): ProductRegisterViewModel => {
  const { isLoading, mutate, reset: resetCreateProduct } = useCreateProduct();
  const { isLoading: isLoadingProductImage, mutate: uploadProductImage } = useUploadProductImage();
  const { isLoading: isLoadingSalesUnit, mutate: uploadSalesUnit } = usePostUSalesUnit();

  const [salesUnits, setSalesUnits] = useState<ProductUnit[]>([emptyUnit()]);
  const [productType, setProductType] = useState<string>("PROCESS");
  const [imageData, setImageData] = useState<File | null>(null);
  const [isRootProduct, setIsRootProduct] = useState<string>(IsRootProduct.NO);

  const isRootProductYes = isRootProduct === IsRootProduct.YES;
  const isRootProductBool = useMemo(
    () => (!DISABLE_IS_ROOT_PRODUCT_BUTTON ? isRootProduct === IsRootProduct.YES : false),
    [isRootProduct]
  );

  const mainForm = useForm<ProductFormData>({ mode: "all" });
  const {
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setValue
  } = mainForm;

  useEffect(() => {
    setIsRootProduct(IsRootProduct.NO);
    return () => {
      setSalesUnits([emptyUnit()]);
      setImageData(null);
      reset();
      resetCreateProduct();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const closeModalStatus = () => {
    const value = getValues();
    setVisibleModal(true, value);
  };

  const saveProductImage = (data: any) => {
    if (imageData) {
      uploadProductImage(
        { product_id: data?._id, file: imageData },
        { onSuccess: () => finishProductImageSave() }
      );
    } else {
      successMessage("Product created successfully");
      closeModalStatus();
    }
  };

  const finishProductImageSave = () => {
    successMessage("Product created successfully");
    closeModalStatus();
  };

  const namedUnits = useMemo(() => salesUnits.filter(u => u.sales_unit_name.trim()), [salesUnits]);

  const saveUnitData = (productData: any) => {
    if (namedUnits.length > 0) {
      const productUnitData = {
        product_id: productData._id,
        sales_units: namedUnits.map(unit => ({
          ...unit,
          sales_unit_name: unit.sales_unit_name.trim()
        }))
      };
      uploadSalesUnit(productUnitData, { onSuccess: () => saveProductImage(productData) });
    }
  };

  const checkProductSaveCondition = (data: ProductFormData): boolean => {
    if (isEmpty(data.product_name.trim())) {
      errorView("Product name is required");
      return true;
    } else if (namedUnits.length <= 0) {
      errorView("Please add product unit sizes", { force: true });
      return true;
    }
    return false;
  };

  const onSubmit = async (productDetails: ProductFormData) => {
    if (!checkProductSaveCondition(productDetails)) {
      const productData = {
        ...productDetails,
        product_name: productDetails.product_name.trim(),
        product_other_info: productDetails.product_other_info?.trim() ?? "",
        product_type: productType,
        sales_units: [],
        product_ingredients: [],
        is_average_product: isRootProductBool
      };
      mutate(productData, {
        onSuccess: data => {
          setValue("productData", { ...data, product_id: data._id });
          saveUnitData(data);
        }
      });
    }
  };

  const canAddUnit = !isRootProductBool || salesUnits.length === 0;

  const onUnitFieldChange = (index: number, patch: Partial<ProductUnit>) => {
    setSalesUnits(prev => prev.map((unit, i) => (i === index ? { ...unit, ...patch } : unit)));
  };

  const onAddUnit = () => {
    if (!canAddUnit) return;
    setSalesUnits(prev => [...prev, emptyUnit()]);
  };

  const onRemoveUnit = (index: number) => {
    if (salesUnits.length === 1) return;
    setSalesUnits(prev => prev.filter((_, i) => i !== index));
  };

  const unitNameHint = (index: number): string | undefined => {
    const clean = (salesUnits[index]?.sales_unit_name || "").trim().toLowerCase();
    if (!clean) return undefined;
    if (clean.length > 20) return "Maximum 20 characters allowed";
    const isDuplicate = salesUnits.some(
      (unit, i) => i !== index && unit.sales_unit_name.trim().toLowerCase() === clean
    );
    return isDuplicate ? "Unit name already exists." : undefined;
  };

  const saveProduct = () => {
    onSubmit(getValues());
  };

  const onCancel = () => {
    closeModalStatus();
    reset();
  };

  return {
    methods: mainForm,
    productType,
    onProductTypeChange: setProductType,
    showAverageProductToggle: !DISABLE_IS_ROOT_PRODUCT_BUTTON,
    isRootProduct,
    onToggleAverageProduct: setIsRootProduct,
    onImageSelect: setImageData,

    unitTypeOptions: ProductRegisterOptions.unitTypes(),
    salesUnits,
    canAddUnit,
    onUnitFieldChange,
    onAddUnit,
    onRemoveUnit,
    unitNameHint,

    isSaving: isLoading || isLoadingSalesUnit || isLoadingProductImage,
    isRootProductYes,
    saveDisabled: errors && Object.keys(errors).length > 0,
    onSave: () => handleSubmit(saveProduct)(),
    onCancel
  };
};
